import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, FileText, ListChecks, AlignLeft, Download, LogOut, CreditCard, User, Database, BookOpen } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import WelcomeModal from "../components/WelcomeModal";
import SaveToNotionModal from "../components/SaveToNotionModal";
import ProjectSelector from "../components/ProjectSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_videogist-3/artifacts/809w15is_summbee%20logo_just%20bee.png";

function DashboardPage() {
  const { user, subscription, logout, refreshSubscription } = useContext(AuthContext);
  const navigate = useNavigate();
  const [videoUrl, setVideoUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [generateBullet, setGenerateBullet] = useState(true);
  const [generateStructured, setGenerateStructured] = useState(true);
  const [summaryLength, setSummaryLength] = useState("standard");
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showNotionModal, setShowNotionModal] = useState(false);
  const [showProjectSelector, setShowProjectSelector] = useState(false);

  useEffect(() => {
    // Check if user needs to complete onboarding
    if (user && !user.onboarding_completed) {
      setShowWelcomeModal(true);
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!videoUrl.trim()) {
      toast.error("Please enter a video URL");
      return;
    }

    if (!generateBullet && !generateStructured) {
      toast.error("Please select at least one extraction type");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(
        `${API}/transcribe`,
        {
          video_url: videoUrl,
          generate_bullet_summary: generateBullet,
          generate_structured_summary: generateStructured,
          summary_length: summaryLength
        },
        { withCredentials: true }
      );
      
      setResult(response.data);
      toast.success("Extraction complete");
      await refreshSubscription();
    } catch (error) {
      console.error("Extraction error:", error);
      if (error.response?.status === 403) {
        toast.error("Monthly limit reached. Upgrade to continue.");
        setTimeout(() => navigate("/pricing"), 2000);
      } else {
        toast.error(error.response?.data?.detail || "Extraction failed. Verify URL and retry.");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleExport = async (format) => {
    if (!result) return;
    
    const content = `${result.video_title}\n\n${result.transcription}`;
    
    try {
      const response = await axios.post(
        `${API}/export/${format}`,
        { title: result.video_title, content },
        { 
          withCredentials: true,
          responseType: format === "pdf" ? "blob" : "text"
        }
      );
      
      if (format === "pdf") {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${result.video_title}.pdf`;
        a.click();
      } else {
        const blob = new Blob([response.data], { type: "text/plain" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${result.video_title}.txt`;
        a.click();
      }
      
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Export unavailable in current tier");
    }
  };
  
  const handleCompleteOnboarding = async (marketingConsent) => {
    try {
      await axios.post(
        `${API}/auth/complete-onboarding`,
        { marketing_consent: marketingConsent },
        { withCredentials: true }
      );
      
      setShowWelcomeModal(false);
      toast.success("Welcome to SummBee! Let's get started.");
      
      // Refresh user data
      window.location.reload();
    } catch (error) {
      console.error("Onboarding error:", error);
      toast.error("Failed to complete setup. Please try again.");
    }
  };

  const hasCustomLength = subscription?.features?.includes("custom_length");
  const canExport = result?.can_export;
  
  // Check if user has Pro tier or above for Notion integration
  const canUseNotion = subscription?.effective_plan && 
    (subscription.effective_plan === "pro" || subscription.effective_plan === "creator" || subscription.in_trial);
  
  return (
    <div className="dashboard-page">
      {showWelcomeModal && <WelcomeModal onComplete={handleCompleteOnboarding} />}
      {showNotionModal && result && (
        <SaveToNotionModal 
          result={result} 
          onClose={() => setShowNotionModal(false)} 
        />
      )}
      {showProjectSelector && result && (
        <ProjectSelector
          videoData={{
            video_title: result.video_title,
            video_url: videoUrl,
            platform: result.platform,
            transcription: result.transcription,
            bullet_summary: result.bullet_summary,
            structured_summary: result.structured_summary
          }}
          onClose={() => setShowProjectSelector(false)}
          onSaved={() => {
            toast.success("Video saved to library!");
            setShowProjectSelector(false);
          }}
        />
      )}
      <nav className="navbar">
        <div className="nav-content">
          <div className="nav-logo" onClick={() => navigate("/")} style={{cursor: "pointer"}}>
            <img src={LOGO_URL} alt="SummBee" className="logo-image" />
            <span>SummBee</span>
          </div>
          <div className="nav-links">
            {canUseNotion && (
              <>
                <Button onClick={() => navigate("/library")} variant="ghost">
                  My Videos
                </Button>
                <Button onClick={() => navigate("/projects")} variant="ghost">
                  Projects
                </Button>
                <Button onClick={() => navigate("/workspaces")} variant="ghost">
                  Workspaces
                </Button>
              </>
            )}
            <div className="user-info" data-testid="user-info">
              <User size={16} />
              <span>{user?.name}</span>
            </div>
            <Button onClick={logout} variant="ghost" data-testid="logout-button" className="logout-button">
              <LogOut size={16} /> Logout
            </Button>
          </div>
        </div>
      </nav>
      
      <div className="dashboard-container">
        {/* Subscription Status */}
        <Card className="subscription-card" data-testid="subscription-card">
          <CardHeader>
            <CardTitle className="card-title">
              <CreditCard className="card-icon" />
              Current Tier
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="subscription-info">
              <div className="plan-badge" data-testid="plan-badge">
                {subscription?.effective_plan?.toUpperCase() || "FREE"}
                {subscription?.in_trial && <span className="trial-badge">TRIAL</span>}
              </div>
              <div className="usage-info">
                <p data-testid="usage-text">
                  {subscription?.videos_remaining === -1 
                    ? "Unlimited extractions available"
                    : `${subscription?.videos_remaining || 0} extractions remaining`
                  }
                </p>
                {subscription?.in_trial && (
                  <p className="trial-text" data-testid="trial-notice">
                    Trial: {subscription.trial_videos_used}/5 Pro extractions used
                  </p>
                )}
              </div>
              {subscription?.plan_type === "free" && !subscription?.in_trial && (
                <Button onClick={() => navigate("/pricing")} data-testid="upgrade-button" className="upgrade-button">
                  Upgrade Tier
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Extraction Form */}
        <Card className="transcribe-card" data-testid="transcribe-form-card">
          <CardHeader>
            <CardTitle>Extract Intelligence</CardTitle>
            <CardDescription>
              Enter video URL from any supported platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="transcribe-form">
              <div className="form-row">
                <Input
                  data-testid="video-url-input"
                  type="url"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  disabled={loading}
                  className="url-input"
                />
                <Button 
                  data-testid="transcribe-submit-button"
                  type="submit" 
                  disabled={loading}
                  className="submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="button-icon animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FileText className="button-icon" />
                      Extract
                    </>
                  )}
                </Button>
              </div>
              
              <div className="form-options">
                <div className="option-group">
                  <Label className="option-label">Extraction Type:</Label>
                  <div className="checkbox-group">
                    <div className="checkbox-item">
                      <Checkbox 
                        id="bullet-summary"
                        data-testid="bullet-summary-checkbox"
                        checked={generateBullet}
                        onCheckedChange={setGenerateBullet}
                        disabled={loading}
                      />
                      <Label htmlFor="bullet-summary" className="checkbox-label">
                        <ListChecks className="checkbox-icon" />
                        Key Points
                      </Label>
                    </div>
                    
                    <div className="checkbox-item">
                      <Checkbox 
                        id="structured-summary"
                        data-testid="structured-summary-checkbox"
                        checked={generateStructured}
                        onCheckedChange={setGenerateStructured}
                        disabled={loading}
                      />
                      <Label htmlFor="structured-summary" className="checkbox-label">
                        <AlignLeft className="checkbox-icon" />
                        Structured Analysis
                      </Label>
                    </div>
                  </div>
                </div>
                
                {hasCustomLength && (
                  <div className="option-group">
                    <Label className="option-label">Depth:</Label>
                    <Select value={summaryLength} onValueChange={setSummaryLength} disabled={loading}>
                      <SelectTrigger className="length-select" data-testid="summary-length-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short">Concise</SelectItem>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="detailed">Comprehensive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
        
        {/* Results */}
        {result && (
          <div className="results-section" data-testid="results-section">
            <div className="results-header">
              <div>
                <h2 className="results-title" data-testid="video-title">{result.video_title}</h2>
                <p className="results-subtitle" data-testid="video-platform">
                  Source: <span className="platform-badge">{result.platform}</span>
                </p>
              </div>
              <div className="export-buttons">
                {canExport && (
                  <>
                    <Button onClick={() => handleExport("pdf")} variant="outline" data-testid="export-pdf-button" className="export-button">
                      <Download size={16} /> PDF
                    </Button>
                    <Button onClick={() => handleExport("txt")} variant="outline" data-testid="export-txt-button" className="export-button">
                      <Download size={16} /> TXT
                    </Button>
                  </>
                )}
                {canUseNotion && (
                  <>
                    <Button 
                      onClick={() => setShowProjectSelector(true)} 
                      variant="outline" 
                      data-testid="save-to-library-button" 
                      className="export-button library-button"
                    >
                      <Database size={16} /> Save to Library
                    </Button>
                    <Button 
                      onClick={() => setShowNotionModal(true)} 
                      variant="outline" 
                      data-testid="save-to-notion-button" 
                      className="export-button notion-button"
                    >
                      <BookOpen size={16} /> Save to Notion
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="results-grid">
              {(result.bullet_summary || result.structured_summary) && (
                <Card className="summary-card" data-testid="summaries-card">
                  <CardHeader>
                    <CardTitle className="card-title">
                      <ListChecks className="card-icon" />
                      Extracted Intelligence
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {result.bullet_summary && result.structured_summary ? (
                      <Tabs defaultValue="bullet" className="summary-tabs">
                        <TabsList className="tabs-list">
                          <TabsTrigger value="bullet" data-testid="bullet-summary-tab">
                            <ListChecks className="tab-icon" />
                            Key Points
                          </TabsTrigger>
                          <TabsTrigger value="structured" data-testid="structured-summary-tab">
                            <AlignLeft className="tab-icon" />
                            Structured
                          </TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="bullet" className="tab-content" data-testid="bullet-summary-content">
                          <div className="summary-content">
                            {result.bullet_summary}
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="structured" className="tab-content" data-testid="structured-summary-content">
                          <div className="summary-content">
                            {result.structured_summary}
                          </div>
                        </TabsContent>
                      </Tabs>
                    ) : result.bullet_summary ? (
                      <div className="summary-content single-summary" data-testid="bullet-summary-content">
                        <div className="summary-header">
                          <ListChecks className="summary-icon" />
                          <h3>Key Points</h3>
                        </div>
                        {result.bullet_summary}
                      </div>
                    ) : (
                      <div className="summary-content single-summary" data-testid="structured-summary-content">
                        <div className="summary-header">
                          <AlignLeft className="summary-icon" />
                          <h3>Structured Analysis</h3>
                        </div>
                        {result.structured_summary}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <Card className="transcription-card" data-testid="transcription-card">
                <CardHeader>
                  <CardTitle className="card-title">
                    <FileText className="card-icon" />
                    Complete Transcription
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="transcription-accordion">
                    <AccordionItem value="transcription">
                      <AccordionTrigger data-testid="transcription-toggle">
                        View full transcript
                      </AccordionTrigger>
                      <AccordionContent data-testid="transcription-content">
                        <div className="transcription-content">
                          {result.transcription}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardPage;
